(() => {
  if (window.PurityDoseScrollDepth || typeof IntersectionObserver === 'undefined' || typeof requestAnimationFrame !== 'function') return;

  const selector = '[data-pd-depth]';
  const surfaceSelector = '[data-pd-depth-surface]';
  const excluded = 'pd-hero-rotation,.pd-ritual-hero,pd-cinematic-scroll,.pd-moments,.pd-about,pd-native-guide,.pd-native-guide,dialog,[role="dialog"],product-modal,quick-add-modal,cart-drawer,menu-drawer,form,product-form,.product-form,.product__info-wrapper,.shopify-payment-button,sticky-header,.section-header,.header-wrapper,nav,[role="navigation"],footer,[data-pd-depth="off"]';
  const interactive = 'a,button,input,select,textarea,summary,details,iframe,video,model-viewer,[tabindex],[contenteditable="true"],[role="button"],[role="link"]';
  const variables = ['--pd-scroll-depth-y', '--pd-scroll-depth-z', '--pd-scroll-depth-angle'];
  const records = new Map();
  const visible = new Set();
  const abort = new AbortController();
  const options = { signal: abort.signal };
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const compact = matchMedia('(max-width: 749px)');
  let frame = 0;
  let suspended = false;
  let destroyed = false;
  let mutationObserver;

  const reset = (record) => {
    record.wrapper.removeAttribute('data-pd-depth-ready');
    record.surface.removeAttribute('data-pd-depth-active');
    variables.forEach((name) => record.surface.style.removeProperty(name));
  };
  const cancelFrame = () => {
    if (frame) cancelAnimationFrame(frame);
    frame = 0;
  };
  const motionAllowed = () => !destroyed && !suspended && !document.hidden && !reduced.matches;
  const remove = (wrapper) => {
    const record = records.get(wrapper);
    if (!record) return;
    reset(record);
    observer.unobserve(wrapper);
    records.delete(wrapper);
    visible.delete(wrapper);
    if (!visible.size) cancelFrame();
  };
  const draw = () => {
    frame = 0;
    if (!motionAllowed()) return;
    const height = Math.max(1, window.innerHeight);
    const amplitude = compact.matches ? { y: 4, z: 2, angle: 0.65 } : { y: 16, z: 8, angle: 3 };
    const updates = [];
    visible.forEach((wrapper) => {
      const record = records.get(wrapper);
      if (!record) return;
      if (!wrapper.isConnected || !wrapper.contains(record.surface)) { remove(wrapper); return; }
      const rect = wrapper.getBoundingClientRect();
      if (!rect.width || !rect.height || wrapper.contains(document.activeElement)) {
        updates.push({ record, static: true });
        return;
      }
      const progress = Math.max(-1, Math.min(1, (height / 2 - rect.top - rect.height / 2) / ((height + rect.height) / 2)));
      updates.push({
        record,
        y: -progress * amplitude.y * record.strength,
        z: (1 - Math.abs(progress)) * amplitude.z * record.strength,
        angle: progress * amplitude.angle * record.strength,
      });
    });
    updates.forEach(({ record, static: isStatic, y, z, angle }) => {
      if (isStatic) { reset(record); return; }
      record.surface.style.setProperty(variables[0], `${y.toFixed(3)}px`);
      record.surface.style.setProperty(variables[1], `${z.toFixed(3)}px`);
      record.surface.style.setProperty(variables[2], `${angle.toFixed(3)}deg`);
      record.surface.setAttribute('data-pd-depth-active', '');
      record.wrapper.setAttribute('data-pd-depth-ready', '');
    });
  };
  const queue = () => {
    if (motionAllowed() && visible.size && !frame) frame = requestAnimationFrame(draw);
  };
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(({ target, isIntersecting }) => {
      if (!records.has(target)) return;
      if (isIntersecting) visible.add(target);
      else {
        visible.delete(target);
        reset(records.get(target));
      }
    });
    if (!visible.size) cancelFrame();
    else queue();
  }, { rootMargin: '80px 0px', threshold: 0 });

  const safeSurface = (surface) => {
    if (!surface || surface.closest(excluded) || surface.matches(interactive) || surface.querySelector(interactive)) return false;
    const imageSurface = surface.matches('img,picture,svg') || (!surface.textContent.trim() && Boolean(surface.querySelector('img,picture,svg')));
    const decoration = surface.getAttribute('aria-hidden') === 'true' && !surface.textContent.trim();
    return imageSurface || decoration;
  };
  const register = (wrapper) => {
    if (!wrapper.isConnected) return;
    const existing = records.get(wrapper);
    if (existing) {
      if (safeSurface(existing.surface) && !wrapper.parentElement?.closest(selector)) return;
      remove(wrapper);
    }
    if (wrapper.closest(excluded) || wrapper.parentElement?.closest(selector)) return;
    const surface = [...wrapper.querySelectorAll(surfaceSelector)].find((node) => node.closest(selector) === wrapper);
    if (!safeSurface(surface)) return;
    const input = wrapper.getAttribute('data-pd-depth-strength');
    const amount = input === null ? 1 : Number(input);
    const strength = Number.isFinite(amount) ? Math.max(0, Math.min(1, amount)) : 1;
    if (!strength) return;
    records.set(wrapper, { wrapper, surface, strength });
    observer.observe(wrapper);
  };
  const refresh = (root = document) => {
    if (destroyed) return;
    const previousCount = records.size;
    records.forEach((record, wrapper) => {
      if (!wrapper.isConnected || !wrapper.contains(record.surface)) remove(wrapper);
    });
    if (root.matches?.(selector)) register(root);
    root.querySelectorAll?.(selector).forEach(register);
    if (records.size !== previousCount) queue();
  };
  const syncMotion = () => {
    cancelFrame();
    records.forEach(reset);
    queue();
  };
  const start = () => {
    if (destroyed) return;
    refresh();
    if (typeof MutationObserver !== 'undefined') {
      mutationObserver = new MutationObserver((changes) => {
        records.forEach((record, wrapper) => {
          if (!wrapper.isConnected || !wrapper.contains(record.surface)) remove(wrapper);
        });
        changes.forEach((change) => {
          const wrapper = change.target.closest?.(selector);
          if (wrapper) refresh(wrapper);
          change.addedNodes.forEach((node) => {
            if (node.nodeType === 1 && !node.matches('script,style')) refresh(node);
          });
        });
      });
      mutationObserver.observe(document.getElementById('MainContent') || document.body, { childList: true, subtree: true });
    }
  };
  window.addEventListener('scroll', queue, { passive: true, ...options });
  window.addEventListener('resize', queue, { passive: true, ...options });
  window.addEventListener('pagehide', () => { suspended = true; syncMotion(); }, options);
  window.addEventListener('pageshow', () => { suspended = false; refresh(); syncMotion(); }, options);
  document.addEventListener('visibilitychange', syncMotion, options);
  document.addEventListener('focusin', (event) => {
    const wrapper = event.target.closest?.(selector);
    if (wrapper && records.has(wrapper)) reset(records.get(wrapper));
  }, options);
  document.addEventListener('focusout', queue, options);
  document.addEventListener('shopify:section:load', (event) => refresh(event.target), options);
  document.addEventListener('shopify:section:unload', (event) => {
    records.forEach((record, wrapper) => {
      if (wrapper === event.target || event.target.contains(wrapper)) remove(wrapper);
    });
  }, options);
  document.addEventListener('shopify:section:reorder', queue, options);
  reduced.addEventListener('change', syncMotion, options);
  compact.addEventListener('change', queue, options);

  window.PurityDoseScrollDepth = {
    refresh,
    destroy() {
      if (destroyed) return;
      destroyed = true;
      cancelFrame();
      abort.abort();
      mutationObserver?.disconnect();
      observer.disconnect();
      records.forEach(reset);
      records.clear();
      visible.clear();
      delete window.PurityDoseScrollDepth;
    },
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true, ...options });
  else start();
})();
